/**
 * Per-project localStorage memory of the currently open article folder.
 * Clone of onboardings' folderMemory (src/modules/onboardings/utils/folderMemory.ts)
 * with an articles-scoped key. The stored folder is validated against the
 * live folder list by the page — a deleted folder resets to root.
 */

export const ARTICLE_FOLDER_MEMORY_VERSION = 1;
export const ARTICLE_FOLDER_MEMORY_KEY_PREFIX = 'segmently.articles.currentFolder.v1';

export interface ArticleFolderMemory {
  version: typeof ARTICLE_FOLDER_MEMORY_VERSION;
  folderId: string | null;
}

export function getArticleFolderMemoryKey(projectId: string): string {
  return `${ARTICLE_FOLDER_MEMORY_KEY_PREFIX}.${projectId}`;
}

export function readArticleFolderMemory(projectId: string): ArticleFolderMemory {
  if (typeof window === 'undefined') {
    return createArticleFolderMemory(null);
  }

  try {
    const rawValue = window.localStorage.getItem(getArticleFolderMemoryKey(projectId));
    if (!rawValue) {
      return createArticleFolderMemory(null);
    }

    const parsed = JSON.parse(rawValue) as Partial<ArticleFolderMemory>;
    if (
      parsed.version !== ARTICLE_FOLDER_MEMORY_VERSION ||
      !('folderId' in parsed) ||
      (parsed.folderId !== null && typeof parsed.folderId !== 'string')
    ) {
      writeArticleFolderMemory(projectId, null);
      return createArticleFolderMemory(null);
    }

    return createArticleFolderMemory(parsed.folderId);
  } catch {
    writeArticleFolderMemory(projectId, null);
    return createArticleFolderMemory(null);
  }
}

export function writeArticleFolderMemory(projectId: string, folderId: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      getArticleFolderMemoryKey(projectId),
      JSON.stringify(createArticleFolderMemory(folderId)),
    );
  } catch {
    // Storage can be unavailable in private browsing or test sandboxes.
  }
}

function createArticleFolderMemory(folderId: string | null): ArticleFolderMemory {
  return {
    version: ARTICLE_FOLDER_MEMORY_VERSION,
    folderId,
  };
}
